import { useState, useEffect } from 'react';
import { Mail, ArrowRight, Check } from 'lucide-react';

const eidDates = [
  { name: 'Eid ul Fitr 2026', date: new Date('2026-03-20T06:00:00'), label: 'Zuckerfest' },
  { name: 'Eid ul Adha 2026', date: new Date('2026-05-27T06:00:00'), label: 'Opferfest' },
];

const getNextEid = () => {
  const now = new Date();
  return eidDates.filter(e => e.date > now).sort((a, b) => a.date - b.date)[0];
};

const calcTimeLeft = (target) => {
  const diff = target - new Date();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const TimeBox = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="font-serif text-4xl sm:text-5xl md:text-6xl font-medium text-[#2C332E] tabular-nums leading-none">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-[10px] sm:text-xs uppercase tracking-widest text-[#6B7280] mt-2">{label}</span>
  </div>
);

export default function ComingSoonPage() {
  const nextEid = getNextEid();
  const [timeLeft, setTimeLeft] = useState(() => nextEid ? calcTimeLeft(nextEid.date) : null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!nextEid) return;
    const target = nextEid.date;
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(target)), 1000);
    return () => clearInterval(timer);
  }, [nextEid]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
      setEmail('');
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="coming-soon-page">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#D4C5A9]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#4A5D4F]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#D4C5A9]/10 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-[#D4C5A9]/10 rounded-full" />
      </div>

      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <div className="w-12 h-12 bg-[#4A5D4F] rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          </div>
          <span className="font-serif text-2xl font-medium text-[#2C332E]">EidGeschenke</span>
        </div>

        {/* Coming Soon */}
        <div className="mb-4">
          <span className="inline-block px-4 py-1.5 bg-[#4A5D4F]/10 rounded-full text-sm font-medium text-[#4A5D4F] mb-6">
            Coming Soon
          </span>
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-medium text-[#2C332E] mb-6 leading-tight">
          Einzigartige Eid Geschenke, bald für dich
        </h1>

        <p className="text-lg text-[#6B7280] leading-relaxed mb-12 max-w-lg mx-auto">
          Wir arbeiten an etwas Besonderem. Handverlesene Geschenke für Eid ul Fitr und Eid ul Adha – liebevoll ausgewählt und schnell geliefert.
        </p>

        {/* Countdown */}
        {timeLeft && (
          <div className="mb-12">
            <p className="text-sm text-[#6B7280] mb-4">
              {nextEid.label} – {nextEid.name}
            </p>
            <div className="flex items-center justify-center gap-4 sm:gap-8">
              <TimeBox value={timeLeft.days} label="Tage" />
              <span className="text-[#D4C5A9] text-3xl font-light mt-[-16px]">:</span>
              <TimeBox value={timeLeft.hours} label="Stunden" />
              <span className="text-[#D4C5A9] text-3xl font-light mt-[-16px]">:</span>
              <TimeBox value={timeLeft.minutes} label="Minuten" />
              <span className="text-[#D4C5A9] text-3xl font-light mt-[-16px] hidden sm:block">:</span>
              <div className="hidden sm:block">
                <TimeBox value={timeLeft.seconds} label="Sekunden" />
              </div>
            </div>
          </div>
        )}

        {/* Email signup */}
        <div className="max-w-md mx-auto">
          {submitted ? (
            <div className="flex items-center justify-center gap-2 bg-[#4A5D4F]/10 rounded-full px-6 py-4" data-testid="coming-soon-success">
              <Check className="w-5 h-5 text-[#4A5D4F]" />
              <span className="text-[#4A5D4F] font-medium">Danke! Wir benachrichtigen dich zum Launch.</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#6B7280] mb-3">Erfahre als Erste/r, wenn wir starten:</p>
              <form onSubmit={handleSubmit} className="flex gap-2" data-testid="coming-soon-form">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="deine@email.de"
                    className="w-full pl-11 pr-4 py-3.5 bg-white border border-[#E5E7EB] rounded-full text-[#2C332E] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#4A5D4F]/20 focus:border-[#4A5D4F] transition-all"
                    data-testid="coming-soon-email"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-[#4A5D4F] text-white px-6 py-3.5 rounded-full font-medium hover:bg-[#3A493E] transition-colors flex items-center gap-2 whitespace-nowrap"
                  data-testid="coming-soon-submit"
                >
                  Benachrichtigen <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer note */}
        <p className="text-xs text-[#9CA3AF] mt-16">
          Kein Spam, versprochen. Du erhältst nur eine E-Mail zum Launch.
        </p>
      </div>
    </main>
  );
}
